import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useBrandName, getTerm, useTerms } from '@/lib/branding';
import { useAuth } from '@/contexts/AuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  BarChart3, FileText, Search, Plus, Clock, Star, StarOff,
  Sparkles, Upload, Layers, TrendingUp, ArrowRight, Loader2, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { RecentImpersonationsPanel } from '@/components/admin/RecentImpersonationsPanel';
import RiskWatchlistTile from '@/components/RiskWatchlistTile';

export default function DashboardPage() {
  useTerms();
  const APP = useBrandName();
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  const [recentWork, setRecentWork] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [aiHighlights, setAiHighlights] = useState([]);
  const [aiInsights, setAiInsights] = useState([]);
  const [insightsSummary, setInsightsSummary] = useState('');
  const [insightsScanning, setInsightsScanning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [highlightsLoading, setHighlightsLoading] = useState(false);
  const [homeDashboard, setHomeDashboard] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        // Check if user has a home dashboard set
        const profile = await api.getUserProfile().catch(() => null);
        if (profile?.home_dashboard_id) {
          setHomeDashboard(profile.home_dashboard_id);
        }

        // Load recent work (reports + visuals sorted by recent)
        const [reports, visuals, favs] = await Promise.all([
          api.getReports().catch(() => []),
          api.getVisuals().catch(() => []),
          api.getFavorites().catch(() => []),
        ]);
        
        // Combine and sort by date
        const combined = [
          ...reports.map(r => ({ ...r, _type: 'insight', _id: r.report_id, _name: r.name, _date: r.updated_at || r.created_at })),
          ...visuals.map(v => ({ ...v, _type: 'visual', _id: v.visual_id, _name: v.name, _date: v.updated_at || v.created_at })),
        ].sort((a, b) => (b._date || '').localeCompare(a._date || '')).slice(0, 10);
        
        setRecentWork(combined);
        setFavorites(favs);
      } catch (err) { console.error('Failed to load home:', err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // Load AI highlights from the user's most-used model
  useEffect(() => {
    if (loading) return;
    setHighlightsLoading(true);
    api.getSemanticModels().then(models => {
      if (models.length > 0) {
        const model = models.find(m => m.status === 'published') || models[0];
        api.exploreSuggestions({ model_id: model.model_id }).then(res => {
          setAiHighlights((res.suggestions || []).slice(0, 3).map((s, i) => ({ id: i, question: s, model_id: model.model_id, model_name: model.name })));
        }).catch(() => {});
        // Load cached AI insights
        api.aiGetInsights(model.model_id).then(res => {
          setAiInsights(res.insights || []);
          setInsightsSummary(res.summary || '');
        }).catch(() => {});
      }
    }).catch(() => {}).finally(() => setHighlightsLoading(false));
  }, [loading]);

  const toggleFavorite = async (itemType, itemId) => {
    try {
      const existing = favorites.find(f => f.item_id === itemId);
      if (existing) {
        await api.removeFavorite(existing.favorite_id);
        setFavorites(prev => prev.filter(f => f.favorite_id !== existing.favorite_id));
        toast.success('Removed from favorites');
      } else {
        const fav = await api.addFavorite({ item_type: itemType, item_id: itemId });
        setFavorites(prev => [...prev, fav]);
        toast.success('Added to favorites');
      }
    } catch (err) { toast.error(err.message); }
  };

  const isFavorited = (itemId) => favorites.some(f => f.item_id === itemId);

  // If user has a home dashboard, redirect to it
  if (homeDashboard) {
    return (
      <div className="p-3 w-full" data-testid="home-page">
        <div className="flex items-center justify-between mb-2">
          <Button variant="ghost" size="sm" onClick={() => setHomeDashboard(null)} className="gap-1.5 text-xs text-muted-foreground">
            <ArrowRight className="w-3 h-3 rotate-180" /> Activity Hub
          </Button>
        </div>
        <iframe src={`/dashboards/view?id=${homeDashboard}&chromeless=1`} className="w-full border rounded-xl" style={{ height: 'calc(100vh - 68px)' }} title="Home Dashboard" />
      </div>
    );
  }

  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  })();

  if (loading) {
    return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto pb-24 md:pb-6" data-testid="home-page">
      {/* Greeting */}
      <div className="mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">{greeting}, {user?.name?.split(' ')[0] || 'there'}</h1>
        <p className="text-xs md:text-sm text-muted-foreground mt-1">Here's what's happening in your workspace</p>
      </div>

      {/* Quick Start Banner — only for users who can access QuickStart */}
      {!loading && hasPermission('quickstart:read') && (
        <div className="mb-6 p-5 rounded-2xl border border-primary/20 bg-primary/[0.04] relative overflow-hidden" data-testid="onboarding-banner">
          <div className="absolute top-0 right-0 w-40 h-40 bg-primary/[0.06] blur-[60px] rounded-full" />
          <div className="relative z-10 flex items-center gap-5">
            <div className="w-12 h-12 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold mb-0.5">Upload data and get instant dashboards</h3>
              <p className="text-xs text-muted-foreground">Drop a CSV and {APP} builds your data model and analytics automatically.</p>
            </div>
            <Button onClick={() => navigate('/quickstart')} size="sm" className="gap-2 flex-shrink-0" data-testid="onboarding-quickstart-btn">
              <Upload className="w-3.5 h-3.5" /> {getTerm('quickstart')}
            </Button>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-6 md:mb-8">
        {hasPermission('visual_library:read') && (
        <button onClick={() => navigate('/visual-library')} className="glass-card rounded-xl p-3 md:p-4 text-left hover:border-primary/30 hover:bg-primary/5 transition-all active:scale-[0.98] group" data-testid="quick-new-visual">
          <div className="p-1.5 md:p-2 rounded-lg bg-primary/10 border border-primary/20 w-fit mb-1.5 md:mb-2 group-hover:bg-primary/20 transition-colors">
            <Plus className="w-4 h-4 md:w-5 md:h-5 text-primary" />
          </div>
          <p className="text-xs md:text-sm font-medium">New {getTerm('visual_library', 'singular')}</p>
          <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 hidden md:block">Create a reusable chart</p>
        </button>
        )}
        {hasPermission('ai_copilot:use') && (
        <button onClick={() => navigate('/ai-copilot')} className="glass-card rounded-xl p-3 md:p-4 text-left hover:border-indigo-500/30 hover:bg-indigo-500/5 transition-all active:scale-[0.98] group" data-testid="quick-explore">
          <div className="p-1.5 md:p-2 rounded-lg bg-indigo-500/10 border border-indigo-500/20 w-fit mb-1.5 md:mb-2 group-hover:bg-indigo-500/20 transition-colors">
            <Search className="w-4 h-4 md:w-5 md:h-5 text-indigo-400" />
          </div>
          <p className="text-xs md:text-sm font-medium">{getTerm('ai_explore')}</p>
          <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 hidden md:block">Ask questions with AI</p>
        </button>
        )}
        {hasPermission('dashboards:read') && (
        <button onClick={() => navigate('/dashboards')} className="glass-card rounded-xl p-3 md:p-4 text-left hover:border-emerald-500/30 hover:bg-emerald-500/5 transition-all active:scale-[0.98] group" data-testid="quick-insights">
          <div className="p-1.5 md:p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 w-fit mb-1.5 md:mb-2 group-hover:bg-emerald-500/20 transition-colors">
            <FileText className="w-4 h-4 md:w-5 md:h-5 text-emerald-400" />
          </div>
          <p className="text-xs md:text-sm font-medium">{getTerm('insights')}</p>
          <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 hidden md:block">Browse dashboards & reports</p>
        </button>
        )}
        {hasPermission('data_sources:read') && (
        <button onClick={() => navigate('/data-sources')} className="glass-card rounded-xl p-3 md:p-4 text-left hover:border-amber-500/30 hover:bg-amber-500/5 transition-all active:scale-[0.98] group" data-testid="quick-import">
          <div className="p-1.5 md:p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 w-fit mb-1.5 md:mb-2 group-hover:bg-amber-500/20 transition-colors">
            <Upload className="w-4 h-4 md:w-5 md:h-5 text-amber-400" />
          </div>
          <p className="text-xs md:text-sm font-medium">Data Sources</p>
          <p className="text-[10px] md:text-xs text-muted-foreground mt-0.5 hidden md:block">Connect or import data</p>
        </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 pb-20 md:pb-0">
        {/* Left column: Recent Work */}
        <div className="lg:col-span-2">
          {/* Super-admin only: recent impersonation sessions for support workflows */}
          <RecentImpersonationsPanel />
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-heading font-semibold text-foreground flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" /> Recent Work
            </h2>
          </div>
          <div className="space-y-2">
            {recentWork.length === 0 ? (
              <div className="glass-card rounded-xl p-8 text-center">
                <BarChart3 className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No recent work yet</p>
                <p className="text-xs text-muted-foreground/80 mt-1">Create a visual or explore your data to get started</p>
              </div>
            ) : recentWork.map(item => (
              <div key={`${item._type}-${item._id}`} className="glass-card rounded-xl p-4 hover:border-primary/20 transition-all cursor-pointer group flex items-center gap-3"
                onClick={() => {
                  if (item._type === 'visual') navigate(`/visual-library/edit?id=${item._id}`);
                  else navigate(`/dashboards/view?id=${item._id}`);
                }} data-testid={`recent-${item._id}`}>
                <div className={`p-2 rounded-lg border ${item._type === 'visual' ? 'bg-primary/10 border-primary/20' : 'bg-emerald-500/10 border-emerald-500/20'}`}>
                  {item._type === 'visual' ? <Layers className="w-4 h-4 text-primary" /> : <FileText className="w-4 h-4 text-emerald-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{item._name || 'Untitled'}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge variant="outline" className="text-[9px]">{item._type === 'visual' ? 'Visual' : item.config?.designerMode === 'report' ? 'Report' : 'Dashboard'}</Badge>
                    <span className="text-[10px] text-muted-foreground">{item._date ? new Date(item._date).toLocaleDateString() : ''}</span>
                  </div>
                </div>
                <button onClick={e => { e.stopPropagation(); toggleFavorite(item._type, item._id); }}
                  className={`p-1.5 rounded-lg transition-all ${isFavorited(item._id) ? 'text-amber-400' : 'text-muted-foreground/30 opacity-0 group-hover:opacity-100'}`}>
                  {isFavorited(item._id) ? <Star className="w-4 h-4 fill-current" /> : <Star className="w-4 h-4" />}
                </button>
                <ArrowRight className="w-4 h-4 text-muted-foreground/30 group-hover:text-primary transition-colors" />
              </div>
            ))}
          </div>
        </div>

        {/* Right column: Favorites + AI Highlights */}
        <div className="space-y-6">
          {/* Risk Watchlist — early-warning signals (renders only when there are runs) */}
          <RiskWatchlistTile />
          {/* Favorites */}
          <div>
            <h2 className="text-sm font-heading font-semibold text-foreground flex items-center gap-2 mb-3">
              <Star className="w-4 h-4 text-amber-400" /> Favorites
            </h2>
            {favorites.length === 0 ? (
              <div className="glass-card rounded-xl p-5 text-center">
                <Star className="w-8 h-8 text-muted-foreground/15 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">No favorites yet</p>
                <p className="text-[10px] text-muted-foreground/70 mt-0.5">Star items to pin them here</p>
              </div>
            ) : (
              <div className="space-y-1.5">
                {favorites.slice(0, 8).map(fav => (
                  <button key={fav.favorite_id || `fav-${fav.item_type}-${fav.item_id}`} onClick={() => {
                    if (fav.item_type === 'visual') navigate(`/visual-library/edit?id=${fav.item_id}`);
                    else navigate(`/dashboards/view?id=${fav.item_id}`);
                  }} className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted/20 text-xs flex items-center gap-2 transition-colors group">
                    <Star className="w-3 h-3 text-amber-400 fill-amber-400 flex-shrink-0" />
                    <span className="truncate flex-1">{fav.item_name || fav.item_id}</span>
                    <span className="text-[9px] text-muted-foreground/70">{fav.item_type}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* AI Proactive Insights */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-heading font-semibold text-foreground flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-400" /> Data Insights
                {insightsScanning && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
              </h2>
              <button onClick={async () => {
                setInsightsScanning(true);
                try {
                  const models = await api.getSemanticModels();
                  const model = models.find(m => m.status === 'published') || models[0];
                  if (model) {
                    const res = await api.aiAnomalyScan({ model_id: model.model_id });
                    setAiInsights(res.insights || []);
                    setInsightsSummary(res.summary || '');
                    toast.success('Scan complete');
                  }
                } catch (err) { toast.error(err.message); }
                finally { setInsightsScanning(false); }
              }} className="text-[9px] text-primary hover:text-primary/80 transition-colors" disabled={insightsScanning}>
                {insightsScanning ? 'Scanning...' : 'Scan Now'}
              </button>
            </div>
            {insightsSummary && <p className="text-[10px] text-muted-foreground mb-2">{insightsSummary}</p>}
            {aiInsights.length > 0 ? (
              <div className="space-y-1.5">
                {aiInsights.slice(0, 5).map((insight, i) => {
                  const sevColors = { critical: 'bg-red-500', warning: 'bg-amber-500', info: 'bg-blue-500', positive: 'bg-emerald-500' };
                  return (
                    <button key={`insight-${i}-${typeof insight.title === 'string' ? insight.title.slice(0, 40) : 'item'}`} onClick={() => {
                      if (insight.suggested_query) {
                        const modelId = aiHighlights[0]?.model_id;
                        if (modelId) {
                          sessionStorage.setItem('exploreAutoAsk', JSON.stringify({ model_id: modelId, question: insight.suggested_query }));
                          navigate('/ai-copilot');
                        }
                      }
                    }} className="w-full text-left glass-card rounded-lg p-2.5 hover:border-primary/20 transition-all group">
                      <div className="flex items-start gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${sevColors[insight.severity] || 'bg-blue-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] font-medium text-foreground">{typeof insight.title === 'object' ? JSON.stringify(insight.title) : insight.title}</p>
                          <p className="text-[9px] text-muted-foreground/70 line-clamp-2 mt-0.5">{typeof insight.description === 'object' ? JSON.stringify(insight.description) : insight.description}</p>
                          {insight.change_pct && typeof insight.change_pct === 'number' && (
                            <Badge variant="outline" className={`text-[8px] mt-1 ${insight.change_pct > 0 ? 'text-emerald-400 border-emerald-500/30' : 'text-red-400 border-red-500/30'}`}>
                              {insight.change_pct > 0 ? '+' : ''}{insight.change_pct}%
                            </Badge>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="glass-card rounded-xl p-5 text-center">
                <TrendingUp className="w-8 h-8 text-muted-foreground/15 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">Click "Scan Now" to analyze your data</p>
              </div>
            )}
          </div>

          {/* AI Highlights */}
          <div>
            <h2 className="text-sm font-heading font-semibold text-foreground flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-indigo-400" /> Quick Insights
              {highlightsLoading && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
            </h2>
            {aiHighlights.length > 0 ? (
              <div className="space-y-2">
                {aiHighlights.map((h, i) => (
                  <button key={`hl-${i}-${h.model_id || 'na'}`} onClick={() => {
                    sessionStorage.setItem('exploreAutoAsk', JSON.stringify({ model_id: h.model_id, question: h.question }));
                    navigate('/ai-copilot');
                  }}
                    className="w-full text-left glass-card rounded-xl p-3 hover:border-indigo-500/20 transition-all group">
                    <p className="text-xs text-foreground group-hover:text-primary transition-colors">{h.question}</p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="glass-card rounded-xl p-5 text-center">
                <Sparkles className="w-8 h-8 text-muted-foreground/15 mx-auto mb-2" />
                <p className="text-xs text-muted-foreground">AI insights will appear here</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
